import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  MenuItem,
  SwipeableDrawer,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Tooltip,
  Divider,
  IconButton,
  Stack
} from '@mui/material';
import React, { useCallback, useMemo, useState, useEffect, useContext } from "react";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ColorPicker from "../../Controls/colorPicker";
import ButtonExportProject from "./buttonExportProject";
import ButtonDeleteProject from "./buttonDeleteProject";
import ProjectVisibilityManagement from "./projectVisibilityManagement";
import { LayoutType } from '../../../types';
import SelectDefaultNodeShape from "./selectDefaultNodeShape";
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import { ProjectContext } from '../../../contexts/ProjectContext';
import { AuthContext } from '../../../contexts/authContext';
import { USER_TYPES } from '../../../constants/userTypes';

// Constants
const DRAWER_WIDTH = 350;
const DEFAULT_BORDER_WIDTH = 2;
const DEFAULT_TEXT_SIZE = 10;
const BORDER_WIDTH_RANGE = { min: 0, max: 10 };
const TEXT_SIZE_RANGE = { min: 0, max: 100 };
const DEFAULT_TEXT_BORDER_WIDTH = 0;
const TEXT_BORDER_WIDTH_RANGE = { min: 0, max: 10 };

// Accordion style constant
const ACCORDION_SX = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
  bgcolor: 'background.paper',
  mb: 1,
  '&::before': { display: 'none' }
};

// Memoized components
const ActionButtons = ({ onSave, onCancel, isSaveDisabled }) => (
  <Stack direction="row" spacing={1.5} sx={{ px: 3, py: 2.5 }}>
    <Button
      variant="outlined"
      color="inherit"
      fullWidth
      onClick={onCancel}
      startIcon={<CloseIcon fontSize="small" />}
    >
      Cancel
    </Button>
    <Button 
      variant="contained" 
      color="success" 
      fullWidth 
      onClick={onSave}
      disabled={isSaveDisabled}
      startIcon={<SaveIcon fontSize="small" />}
    >
      Save changes
    </Button>
  </Stack>
);

const panelPaddingSx = {
  px: 2,
  py: 2.5
};

const GeneralSettingsPanel = ({ projectData, onProjectDataChange }) => (
  <Stack spacing={2} sx={panelPaddingSx}>
    <TextField
      id="name"
      name="name"
      label="Project Name"
      size="small"
      fullWidth
      required
      value={projectData?.name ?? ''}
      onChange={onProjectDataChange}
      inputProps={{ maxLength: 100 }}
      helperText={`${(projectData?.name ?? '').length}/100 characters`}
      error={!projectData?.name?.trim()}
    />
    <TextField
      id="description"
      name="description"
      label="Project Description"
      size="small"
      fullWidth
      multiline
      rows={4}
      value={projectData?.description ?? ''}
      onChange={onProjectDataChange}
      inputProps={{ maxLength: 500 }}
      helperText={`${(projectData?.description ?? '').length}/500 characters`}
    />
    <TextField
      id="layout"
      select
      label="Layout"
      size="small"
      name="layout"
      value={projectData?.layout ?? ''}
      onChange={onProjectDataChange}
      fullWidth
    >
      {LayoutType.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  </Stack>
);

const NodeAppearancePanel = ({ projectData, onProjectDataChange }) => (
  <Stack spacing={2} sx={panelPaddingSx}>
    <Typography variant="body2" color="text.secondary">
      These settings apply to new nodes and those without custom appearance adjustments.
    </Typography>
    <SelectDefaultNodeShape handleChange={onProjectDataChange} value={projectData?.defaultshapetype} />
    <ColorPicker 
      label="Background color" 
      name="themeelementcolor" 
      handleChange={onProjectDataChange} 
      color={projectData?.themeelementcolor}
    />
    <ColorPicker 
      label="Border color" 
      name="themebordercolor" 
      handleChange={onProjectDataChange} 
      color={projectData?.themebordercolor}
    />
    <TextField
      id="themeborderwidth"
      name="themeborderwidth"
      label="Border Width"
      type="number"
      value={projectData?.themeborderwidth ?? DEFAULT_BORDER_WIDTH}
      InputLabelProps={{ shrink: true }}
      InputProps={{ 
        inputProps: { 
          min: BORDER_WIDTH_RANGE.min, 
          max: BORDER_WIDTH_RANGE.max 
        } 
      }}
      sx={{ width: 160 }}
      onChange={onProjectDataChange}
    />
  </Stack>
);

const EdgeAppearancePanel = ({ projectData, onProjectDataChange }) => (
  <Stack spacing={2} sx={panelPaddingSx}>
    <Typography variant="body2" color="text.secondary">
      These settings affect newly created edges.
    </Typography>
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(projectData?.themeedgeshowlabel)}
          name="themeedgeshowlabel"
          onChange={onProjectDataChange}
        />
      }
      label="Show edge label"
      sx={{ m: 0 }}
    />
    <ColorPicker 
      label="Edge color" 
      name="themeedgecolor" 
      handleChange={onProjectDataChange} 
      color={projectData?.themeedgecolor}
    />
  </Stack>
);

const TextSettingsPanel = ({ projectData, onProjectDataChange }) => (
  <Stack spacing={2} sx={panelPaddingSx}>
    <Typography variant="body2" color="text.secondary">
      Control default typography for node labels.
    </Typography>
    <FormControlLabel
      control={
        <Switch
          checked={Boolean(projectData?.themetextshowlabel)}
          name="themetextshowlabel"
          onChange={onProjectDataChange}
        />
      }
      label="Show node label"
      sx={{ m: 0 }}
    />
    <ColorPicker 
      label="Text color" 
      name="themetextcolor" 
      handleChange={onProjectDataChange} 
      color={projectData?.themetextcolor}
    />
    <ColorPicker 
      label="Text border color" 
      name="themetextbordercolor" 
      handleChange={onProjectDataChange} 
      color={projectData?.themetextbordercolor}
    />
    <TextField
      id="themetextsize"
      name="themetextsize"
      label="Text Size"
      type="number"
      value={projectData?.themetextsize ?? DEFAULT_TEXT_SIZE}
      InputLabelProps={{ shrink: true }}
      InputProps={{ 
        inputProps: { 
          min: TEXT_SIZE_RANGE.min, 
          max: TEXT_SIZE_RANGE.max 
        } 
      }}
      sx={{ width: 160 }}
      onChange={onProjectDataChange}
    />
    <TextField
      id="themetextborderwidth"
      name="themetextborderwidth"
      label="Border Width"
      type="number"
      value={projectData?.themetextborderwidth ?? DEFAULT_TEXT_BORDER_WIDTH}
      InputLabelProps={{ shrink: true }}
      InputProps={{ 
        inputProps: { 
          min: TEXT_BORDER_WIDTH_RANGE.min, 
          max: TEXT_BORDER_WIDTH_RANGE.max 
        } 
      }}
      sx={{ width: 160 }}
      onChange={onProjectDataChange}
    />
  </Stack>
);

const ProjectDrawer = ({ project, state, setState, update, canvasRef }) => {
  const { importElementsFromJson } = useContext(ProjectContext);
  const { userProfile } = useContext(AuthContext);
  const [expanded, setExpanded] = useState('panel1');
  const [expandedAppearance, setExpandedAppearance] = useState('panelAppearance1');
  const [projectData, setProjectData] = useState(project || {});

  useEffect(() => {
    setProjectData(project ? { ...project } : {});
  }, [project]);

  const handleProjectDataChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const parsedValue = type === 'checkbox'
      ? checked
      : type === 'number'
        ? (value === '' ? '' : Number(value))
        : value;
    setProjectData(prev => ({
      ...prev,
      [name]: parsedValue,
    }));
  }, []);

  const handleChange = useCallback((panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  }, []);

  const handleChangeAppearance = useCallback((panel) => (event, isExpanded) => {
    setExpandedAppearance(isExpanded ? panel : false);
  }, []);

  const handleSaveButtonClick = useCallback(() => {
    if (!projectData?.name?.trim()) return;
    update(projectData);
    setState(false);
    // Refresh layout if canvasRef.current and its setLayout are available
    if (canvasRef?.current && typeof canvasRef.current.setLayout === "function") {
      canvasRef.current.setLayout();
    }
  }, [update, projectData, setState]);

  const handleCancelButtonClick = useCallback(() => {
    setState(false);
  }, [setState]);

  const toggleDrawer = useCallback((open) => (event) => {
    if (
      event &&
      event.type === 'keydown' &&
      (event.key === 'Tab' || event.key === 'Shift')
    ) {
      return;
    }
    setState(open);
  }, [setState]);

  const isSaveDisabled = useMemo(() => !projectData?.name?.trim(), [projectData?.name]);

  const accordionItems = useMemo(() => [
    {
      id: 'panel1',
      title: 'General settings',
      content: <GeneralSettingsPanel projectData={projectData} onProjectDataChange={handleProjectDataChange} />
    },
    {
      id: 'panel2',
      title: 'Appearance',
      content: (
        <Stack spacing={2.5} sx={panelPaddingSx}>
          <ColorPicker 
            label="Background color" 
            name="backgroundcolor" 
            handleChange={handleProjectDataChange} 
            color={projectData?.backgroundcolor}
          />
          <TextField
            id="elementsize"
            name="elementsize"
            label="Element Size"
            size="small"
            type="number"
            fullWidth
            value={projectData?.elementsize ?? 100}
            onChange={handleProjectDataChange}
            InputProps={{ 
              inputProps: { 
                min: 50, 
                max: 300 
              } 
            }}
          />
          <TextField
            id="elementspacing"
            name="elementspacing"
            label="Element Spacing"
            size="small"
            type="number"
            fullWidth
            value={projectData?.elementspacing ?? 3}
            onChange={handleProjectDataChange}
            InputProps={{ 
              inputProps: { 
                min: 1, 
                max: 50 
              } 
            }}
          />
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(projectData?.dynamicsize)}
                  name="dynamicsize"
                  onChange={handleProjectDataChange}
                />
              }
              label="Dynamic size"
              sx={{ m: 0 }}
            />
            <Tooltip title="Node size adjusts based on the number of children." enterTouchDelay={0}>
              <InfoOutlinedIcon fontSize="inherit" sx={{ color: 'text.secondary' }} />
            </Tooltip>
          </Stack>
          <Accordion sx={ACCORDION_SX} expanded={expandedAppearance === 'panelAppearance1'} onChange={handleChangeAppearance('panelAppearance1')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ flexShrink: 0, fontWeight: 600 }}>Default node appearance</Typography>
            </AccordionSummary>
            <NodeAppearancePanel projectData={projectData} onProjectDataChange={handleProjectDataChange} />
          </Accordion>

          <Accordion sx={ACCORDION_SX} expanded={expandedAppearance === 'panelAppearance2'} onChange={handleChangeAppearance('panelAppearance2')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ flexShrink: 0, fontWeight: 600 }}>Default edge appearance</Typography>
            </AccordionSummary>
            <EdgeAppearancePanel projectData={projectData} onProjectDataChange={handleProjectDataChange} />
          </Accordion>

          <Accordion sx={ACCORDION_SX} expanded={expandedAppearance === 'panelAppearance3'} onChange={handleChangeAppearance('panelAppearance3')}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography sx={{ flexShrink: 0, fontWeight: 600 }}>Default text settings</Typography>
            </AccordionSummary>
            <TextSettingsPanel projectData={projectData} onProjectDataChange={handleProjectDataChange} />
          </Accordion>
        </Stack>
      )
    },
    {
      id: 'panel3',
      title: 'Visibility',
      content: <Stack spacing={2} sx={panelPaddingSx}><ProjectVisibilityManagement projectData={projectData} handleChange={handleProjectDataChange} /></Stack>
    },
    {
      id: 'panel5',
      title: 'Project',
      content: (
        <Stack spacing={2} sx={panelPaddingSx}>
          <ButtonExportProject canvasRef={canvasRef} />
          {userProfile?.type > USER_TYPES.FREE ? (
            <Button
              variant="outlined"
              color="primary"
              component="label"
              fullWidth
            >
              Import elements from JSON
              <input
                type="file"
                accept="application/json"
                hidden
                onChange={async (e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  try {
                    const text = await file.text();
                    let jsonData;
                    try {
                      jsonData = JSON.parse(text);
                    } catch (err) {
                      alert("Failed to parse JSON: " + err.message);
                      return;
                    }
                    if (importElementsFromJson) {
                      await importElementsFromJson(jsonData);
                    } else {
                      alert("Import function not available. Please ensure you're using the Project drawer within ProjectContext.");
                    }
                  } catch (error) {
                    alert("Failed to import: " + error.message);
                  }
                  // reset file input so you can pick the same file again
                  e.target.value = "";
                }}
              />
            </Button>
          ) : (
            <Tooltip title="Element import is only available for Supported or Admin users.">
              <span>
                <Button
                  variant="outlined"
                  color="primary"
                  fullWidth
                  disabled
                >
                  Import elements from JSON
                </Button>
              </span>
            </Tooltip>
          )}
          <ButtonDeleteProject />
        </Stack>
      )
    }
  ], [projectData, handleProjectDataChange, expandedAppearance, handleChangeAppearance, canvasRef, importElementsFromJson, userProfile?.type]);

  return (
    <SwipeableDrawer
      anchor="left"
      open={state}
      onClose={toggleDrawer(false)}
      onOpen={toggleDrawer(true)}
    >
      <Box
        sx={{
          width: DRAWER_WIDTH,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          bgcolor: 'background.default'
        }}
        role="presentation"
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 2.5
          }}
        >
          <Box>
            <Typography variant="h6">
              Project settings
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Adjust defaults, appearance, and collaboration preferences.
            </Typography>
          </Box>
          <IconButton
            edge="end"
            aria-label="Close drawer"
            onClick={handleCancelButtonClick}
            size="small"
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Divider />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: 2,
            py: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5
          }}
        >
          {accordionItems.map(({ id, title, content }) => (
            <Accordion
              key={id}
              expanded={expanded === id}
              onChange={handleChange(id)}
              sx={ACCORDION_SX}
            >
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls={`${id}-content`}
                id={`${id}-header`}
              >
                <Typography sx={{ flexShrink: 0, fontWeight: 600 }}>{title}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ px: 0, pb: 0 }}>
                {content}
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
        <Divider />
        <ActionButtons onSave={handleSaveButtonClick} onCancel={handleCancelButtonClick} isSaveDisabled={isSaveDisabled} />
      </Box>
    </SwipeableDrawer>
  );
};

ProjectDrawer.displayName = 'ProjectDrawer';

export default ProjectDrawer;